import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { UpdateChannelMediaRequest } from '@troia-v3/types';

import { useSocket } from '@/contexts/SocketContext';
import { channelDashboardApi, type CreateMediaBody, type ListMediaParams } from '../services/channelDashboardApi';

const mediaKey = (channelId: string | undefined, params?: ListMediaParams): unknown[] =>
  ['channel-dashboard', channelId, 'media', params ?? {}];

export const useChannelMediaList = (channelId: string | undefined, params?: ListMediaParams, enabled = true) =>
  useQuery({
    queryKey: mediaKey(channelId, params),
    queryFn: () => channelDashboardApi.listMedia(channelId!, params),
    enabled: !!channelId && enabled,
    staleTime: 30_000,
  });

const invalidateMedia = (queryClient: ReturnType<typeof useQueryClient>, channelId: string | undefined): void => {
  void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId, 'media'], exact: false });
};

export const useCreateChannelMedia = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMediaBody) => channelDashboardApi.createMedia(channelId!, body),
    onSuccess: () => {
      invalidateMedia(queryClient, channelId);
    },
    onError: () => {
      toast.error('Não foi possível salvar a publicação');
    },
  });
};

export const useUpdateChannelMedia = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, body }: { mediaId: string; body: UpdateChannelMediaRequest }) =>
      channelDashboardApi.updateMedia(channelId!, mediaId, body),
    onSuccess: () => {
      invalidateMedia(queryClient, channelId);
    },
    onError: () => {
      toast.error('Não foi possível atualizar a publicação');
    },
  });
};

export const usePublishChannelMedia = (channelId: string | undefined, providerLabel: 'Facebook' | 'Instagram' = 'Instagram') => {
  const queryClient = useQueryClient();
  const refresh = (): void => {
    invalidateMedia(queryClient, channelId);
    void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId, 'overview'] });
  };
  return useMutation({
    // O backend retorna 200 (success:true) ou 502 (falha) → 502 cai em onError.
    mutationFn: (mediaId: string) => channelDashboardApi.publishMedia(channelId!, mediaId),
    onSuccess: () => {
      toast.success(`Publicado no ${providerLabel}`);
      refresh();
    },
    onError: (error) => {
      const ax = error as { response?: { data?: { message?: string; data?: { error?: string } } } };
      toast.error(ax.response?.data?.message ?? ax.response?.data?.data?.error ?? 'Falha ao publicar');
      refresh(); // o item já foi marcado 'failed' no backend — refletir na fila
    },
  });
};

export const useDeleteChannelMedia = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) => channelDashboardApi.deleteMedia(channelId!, mediaId),
    onSuccess: () => {
      toast.success('Publicação removida');
      invalidateMedia(queryClient, channelId);
    },
    onError: () => {
      toast.error('Não foi possível remover');
    },
  });
};

/**
 * Escuta channel:media-published para ESTE canal e invalida a lista de mídia + overview.
 * String literal no socket.on (convenção do módulo channels — bug Vite pre-bundle).
 */
export const useChannelMediaSocket = (channelId: string | undefined): void => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!socket || !channelId) return;
    const onPublished = (e: { channelId: string }): void => {
      if (e.channelId !== channelId) return;
      invalidateMedia(queryClient, channelId);
      void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId, 'overview'] });
    };
    socket.on('channel:media-published', onPublished);
    return () => {
      socket.off('channel:media-published', onPublished);
    };
  }, [socket, channelId, queryClient]);
};
